import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './CategoryPage.css';

export default function CategoryPage() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState('default');
  const { categorySlug } = useParams();

  useEffect(() => {
    fetchData();
  }, [categorySlug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch category first
      const categoryResponse = await fetch(`${API_BASE_URL}/api/categories?slug=${categorySlug}`);
      if (!categoryResponse.ok) {
        throw new Error('Nie udało się pobrać kategorii');
      }
      const categoryData = await categoryResponse.json();
      if (!categoryData || categoryData.length === 0) {
        throw new Error('Nie znaleziono kategorii');
      }
      setCategory(categoryData[0]);

      // Then fetch products
      const productsResponse = await fetch(`${API_BASE_URL}/api/products?category=${categorySlug}`);
      if (!productsResponse.ok) {
        throw new Error('Nie udało się pobrać produktów z tej kategorii');
      }
      const productsData = await productsResponse.json();
      setProducts(productsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Wystąpił problem podczas ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  // Sortowanie produktów zgodnie z wybraną opcją
  const getSortedProducts = () => {
    if (!products) return [];
    
    const sortedProducts = [...products];
    
    switch (sortOption) {
      case 'price-asc':
        return sortedProducts.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return sortedProducts.sort((a, b) => b.price - a.price);
      case 'name-asc':
        return sortedProducts.sort((a, b) => a.displayName.localeCompare(b.displayName));
      case 'name-desc':
        return sortedProducts.sort((a, b) => b.displayName.localeCompare(a.displayName));
      case 'newest':
        return sortedProducts.sort((a, b) => {
          const dateA = a.dateAdded ? new Date(a.dateAdded) : new Date(0);
          const dateB = b.dateAdded ? new Date(b.dateAdded) : new Date(0);
          return dateB - dateA;
        });
      default:
        return sortedProducts;
    }
  };

  if (loading) {
    return (
      <div className="category-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Ładowanie produktów...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-page">
        <div className="error-container">
          <h2>Wystąpił błąd</h2>
          <p>{error}</p>
          <button onClick={fetchData}>Spróbuj ponownie</button>
        </div>
      </div>
    );
  }

  const sortedProducts = getSortedProducts();

  return (
    <div className="category-page">
      <div className="category-header">
        <h1>{category ? category.name : categorySlug}</h1>
        <div className="sorting-controls">
          <label htmlFor="sort">Sortuj według:</label>
          <select id="sort" value={sortOption} onChange={handleSortChange}>
            <option value="default">Domyślnie</option>
            <option value="price-asc">Cena: od najniższej</option>
            <option value="price-desc">Cena: od najwyższej</option>
            <option value="name-asc">Nazwa: A-Z</option>
            <option value="name-desc">Nazwa: Z-A</option>
            <option value="newest">Najnowsze</option>
          </select>
        </div>
      </div>

      {sortedProducts.length === 0 ? (
        <div className="no-products">
          <p>Brak produktów w tej kategorii</p>
        </div>
      ) : (
        <div className="products-grid">
          {sortedProducts.map(product => (
            <Link 
              to={`/product/${product.slug}`} 
              className="product-card" 
              key={product._id}
            >
              <div className="product-image">
                <img 
                  src={product.imageUrls?.[0] || '/placeholder.png'} 
                  alt={product.displayName}
                  onError={(e) => {e.target.src = '/placeholder.png'}}
                />
              </div>
              <div className="product-info">
                <h3>{product.displayName}</h3>
                <p className="product-price">{product.price.toFixed(2)} zł</p>
                {product.stock <= 0 ? (
                  <span className="out-of-stock">Produkt niedostępny</span>
                ) : (
                  <span className="in-stock">Dostępny</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
} 